import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Gift, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * BusinessCustomerLoyaltyCard
 *
 * Renders the business-side view of a single customer's loyalty stamp card.
 * Shows visits / earned / redeemed / available and lets the owner mark a
 * reward as collected in-store via `POST /me/business/loyalty/redeem`.
 */
export default function BusinessCustomerLoyaltyCard({ customerId, customerName }) {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/me/business/loyalty/card/${customerId}`);
      setCard(data);
    } catch (err) {
      // 403 for non-business viewers, 404 for missing customer — swallow both
      setCard(null);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return null;
  if (!card || !card.loyalty_enabled) return null; // hide entirely when the business has no loyalty program

  const { visits, visits_required, progress, rewards_earned, rewards_redeemed, rewards_available, reward_text } = card;
  const percent = visits_required > 0 ? Math.min(100, Math.round((progress / visits_required) * 100)) : 0;

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/me/business/loyalty/redeem", { customer_id: customerId, note: note.trim() || undefined });
      toast.success("Loyalty reward marked as redeemed");
      setOpen(false);
      setNote("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="rounded-2xl border border-border mt-8 p-6 shadow-sm bg-white" data-testid="business-customer-loyalty-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="h-8 w-1 rounded bg-[#B31E1E]" aria-hidden="true" />
            <Gift size={16} className="text-[#B31E1E]" />
            <h3 className="font-display text-2xl">Loyalty stamp card</h3>
            {rewards_available > 0 && (
              <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px] font-mono" data-testid="loyalty-available-badge">
                {rewards_available} unredeemed reward{rewards_available === 1 ? "" : "s"}
              </Badge>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1 ml-3">
            {reward_text ? <>Reward: <span className="text-gray-900">{reward_text}</span></> : "Track this customer's loyalty visits at your business."}
          </p>
        </div>
        <Button
          type="button"
          onClick={() => setOpen(true)}
          disabled={rewards_available < 1}
          className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]"
          data-testid="loyalty-redeem-button"
          title={rewards_available < 1 ? "Customer has no unredeemed rewards yet" : "Mark reward as collected"}
        >
          <Check size={14} className="mr-1.5" />
          {rewards_available < 1 ? "No reward to redeem" : "Redeem reward"}
        </Button>
      </div>

      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Visits", value: visits, testId: "loyalty-stat-visits" },
          { label: "Earned", value: rewards_earned, testId: "loyalty-stat-earned" },
          { label: "Redeemed", value: rewards_redeemed, testId: "loyalty-stat-redeemed" },
          { label: "Available", value: rewards_available, testId: "loyalty-stat-available", accent: rewards_available > 0 },
        ].map((s) => (
          <div key={s.label} className={`rounded-md border ${s.accent ? "border-emerald-300 bg-emerald-50" : "border-border bg-gray-50"} px-3 py-2`} data-testid={s.testId}>
            <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500">{s.label}</div>
            <div className={`text-2xl font-display ${s.accent ? "text-emerald-800" : "text-gray-900"}`}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between text-[11px] font-mono text-gray-500 mb-1">
          <span>Progress toward next reward</span>
          <span data-testid="loyalty-progress-label">{progress}/{visits_required}</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#B31E1E] transition-all"
            style={{ width: `${percent}%` }}
            data-testid="loyalty-progress-bar"
          />
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="loyalty-redeem-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Redeem loyalty reward</DialogTitle>
            <DialogDescription>
              Confirm <strong>{customerName || "this customer"}</strong> is
              collecting their reward{reward_text ? ": " + reward_text : ""}. This
              will decrement their unredeemed count by 1 and add an entry to
              your Redemptions log.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <label className="block">
              <span className="text-xs font-mono uppercase tracking-wider text-gray-500">
                Note (optional — shows on your log)
              </span>
              <Textarea
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Free 12oz latte, Tuesday special"
                className="rounded-md mt-1"
                data-testid="loyalty-redeem-note"
              />
            </label>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={saving}
              className="rounded-[35px]"
              data-testid="loyalty-redeem-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={saving}
              className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]"
              data-testid="loyalty-redeem-confirm"
            >
              {saving ? (<><Loader2 size={14} className="mr-1.5 animate-spin" /> Redeeming…</>) : (<><Check size={14} className="mr-1.5" /> Confirm redemption</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
